import React, { useEffect, useState } from 'react';
import { Button } from '../../../../../../ui';

interface WeightingItem {
  label: string;
  value: number;
  percent: number;
}

const initialItems: WeightingItem[] = [
  { label: 'Location', value: 15, percent: 0 },
  { label: 'Minimum Work Experience', value: 20, percent: 0 },
  { label: 'Mandatory Skills', value: 25, percent: 0 },
  { label: 'Desirable Skills', value: 15, percent: 0 },
  { label: 'Behavioral Attributes', value: 25, percent: 0 },
];

const defaultWeight = parseFloat((100 / initialItems.length).toFixed(2));

interface AIScoreWeightingModel {
  aiScoreWeights?: {
    location?: number;
    minimumWorkExperience?: number;
    mandatorySkills?: number;
    desirableSkills?: number;
    keyword?: number;
    behavioralAttributes?: number;
  };
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    weights: {
      location: number;
      minimumWorkExperience: number;
      mandatorySkills: number;
      desirableSkills: number;
      behavioralAttributes: number;
    } | null
  ) => void;
}

export const AIScoreWeightingModal = (props: AIScoreWeightingModel) => {
  const { isOpen, onClose, onSave, aiScoreWeights } = props;
  // Equal weighting toggle, default disabled
  const [equalWeighting, setEqualWeighting] = useState<boolean>(false);
  // Custom weighting enabled/disabled (default enabled)
  const [weightingEnabled, setWeightingEnabled] = useState<boolean>(true);
  const [items, setItems] = useState<WeightingItem[]>(() => {
    if (aiScoreWeights) {
      const total =
        (aiScoreWeights.location ?? 15) +
        (aiScoreWeights.minimumWorkExperience ?? 20) +
        (aiScoreWeights.mandatorySkills ?? 25) +
        (aiScoreWeights.desirableSkills ?? 15) +
        (aiScoreWeights.behavioralAttributes ?? 25);

      return [
        {
          label: 'Location',
          value: aiScoreWeights.location ?? 15,
          percent: total === 0 ? 0 : parseFloat((((aiScoreWeights.location ?? 15) / total) * 100).toFixed(1)),
        },
        {
          label: 'Minimum Work Experience',
          value: aiScoreWeights.minimumWorkExperience ?? 20,
          percent: total === 0 ? 0 : parseFloat((((aiScoreWeights.minimumWorkExperience ?? 20) / total) * 100).toFixed(1)),
        },
        {
          label: 'Mandatory Skills',
          value: aiScoreWeights.mandatorySkills ?? 25,
          percent: total === 0 ? 0 : parseFloat((((aiScoreWeights.mandatorySkills ?? 25) / total) * 100).toFixed(1)),
        },
        {
          label: 'Desirable Skills',
          value: aiScoreWeights.desirableSkills ?? 15,
          percent: total === 0 ? 0 : parseFloat((((aiScoreWeights.desirableSkills ?? 15) / total) * 100).toFixed(1)),
        },
        {
          label: 'Behavioral Attributes',
          value: aiScoreWeights.behavioralAttributes ?? 25,
          percent: total === 0 ? 0 : parseFloat((((aiScoreWeights.behavioralAttributes ?? 25) / total) * 100).toFixed(1)),
        },
      ];
    }
    // Default: custom weighting with calculated percentages
    const total = initialItems.reduce((sum, item) => sum + item.value, 0);
    return initialItems.map((item) => ({
      ...item,
      percent: total === 0 ? 0 : parseFloat(((item.value / total) * 100).toFixed(1)),
    }));
  });

  // Calculate percentages whenever values change
  const calculatePercentages = (itemsList: WeightingItem[]) => {
    const total = itemsList.reduce((sum, item) => sum + item.value, 0);
    return itemsList.map((item) => ({
      ...item,
      percent:
        total === 0 ? 0 : parseFloat(((item.value / total) * 100).toFixed(1)),
    }));
  };

  // Handle equal weighting toggle
  useEffect(() => {
    if (equalWeighting) {
      // Set all values to equal with percentages
      const equalItems = items.map((item) => ({
        ...item,
        value: defaultWeight,
      }));
      setItems(calculatePercentages(equalItems));
    } else {
      // Restore custom defaults with percentages
      const customItems = initialItems.map((item) => ({ ...item }));
      setItems(calculatePercentages(customItems));
    }
  }, [equalWeighting]);

  const handleValueChange = (index: number, newValue: number) => {
    const updated = [...items];
    updated[index].value = newValue;
    setItems(calculatePercentages(updated));
  };

  if (!isOpen) return null;

  return (
    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'>
      <div className='bg-white rounded-md p-6 w-full max-w-lg shadow-xl'>
        <div className='flex items-start justify-between mb-8'>
          <span className='font-semibold text-green-800'>
            CV Score Weighting
          </span>
          <div className='flex  flex-col gap-4'>
            <div className='flex justify-between items-center'>
              <span className='text-xs mr-2'>Equal Weighting</span>
              <button
                  onClick={() => {
                    setEqualWeighting((prev) => !prev);
                    setWeightingEnabled(false);
                  }}
                  className={`w-10 h-5 flex items-center rounded-full transition-colors duration-300 ${
                    equalWeighting ? 'bg-green-600' : 'bg-gray-300'
                  }`}
              >
                <div
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${
                      equalWeighting ? 'translate-x-5' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
            <div className='flex justify-between items-center'>
              <span className='text-xs mr-2'>Custom Weighting</span>
              <button
                  onClick={() => {
                    setWeightingEnabled((prev) => !prev);
                    setEqualWeighting(false);
                  }}
                  className={`w-10 h-5 flex items-center rounded-full transition-colors duration-300 ${
                    weightingEnabled ? 'bg-green-600' : 'bg-gray-300'
                  }`}
              >
                <div
                    className={`bg-white w-4 h-4 rounded-full shadow-md transform transition-transform duration-300 ${
                      weightingEnabled ? 'translate-x-5' : 'translate-x-1'
                    }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className='space-y-8'>
          { items.map((item, idx) => (
            <div
                key={idx}
                className='grid grid-cols-[1fr_auto_auto] items-center '
            >
              <span className='text-sm text-gray-700'>{ item.label }</span>
              <input
                  type='number'
                  min={0}
                  max={100}
                  step='0.1'
                  value={item.value}
                  onChange={(e) =>
                    handleValueChange(
                      idx,
                      Math.min(parseFloat(e.target.value) || 0, 100),
                    )
                  }
                  className='w-16 text-sm text-center border border-gray-300 rounded px-2 py-1'
                  disabled={equalWeighting ? false : !weightingEnabled}
              />
              <div className='text-sm text-gray-600 text-right w-12'>
                { item.percent }%
              </div>
            </div>
          )) }
        </div>
        <div className='flex justify-end mt-10 gap-3'>
          <Button onClick={onClose} variant='secondary' label='Cancel' />
          <Button
              onClick={() => {
                if (equalWeighting) {
                // Save equal weights
                  const weights = {
                    location: defaultWeight,
                    minimumWorkExperience: defaultWeight,
                    mandatorySkills: defaultWeight,
                    desirableSkills: defaultWeight,
                    behavioralAttributes: defaultWeight,
                  };
                  onSave(weights);
                } else if (weightingEnabled) {
                // Save custom weights
                  const weights = {
                    location: items[0].value,
                    minimumWorkExperience: items[1].value,
                    mandatorySkills: items[2].value,
                    desirableSkills: items[3].value,
                    behavioralAttributes: items[4].value,
                  };
                  onSave(weights);
                } else {
                // Disabled: use custom defaults
                  onSave({
                    location: 15,
                    minimumWorkExperience: 20,
                    mandatorySkills: 25,
                    desirableSkills: 15,
                    behavioralAttributes: 25,
                  });
                }
                onClose();
              }}
              variant='primary'
              label='Save'
          />
        </div>
      </div>
    </div>
  );
};

export default AIScoreWeightingModal;
